import { useEffect, useState } from "react";
import { Link } from "react-router";
import modules from "./orbModules";
import ModulePlan from "./ModulePlan";
import MWHLoadingScreenLogo from "../../assets/images/MWHLoadingScreenLogo.png";
import SiteMaintenanceIcon from "../../assets/images/maintenance.png";
import AssetManagerIcon from "../../assets/images/library.png";
import ObjectManagerIcon from "../../assets/images/properties.png";
import PidIcon from "../../assets/images/pump.png";
import ModelViewerIcon from "../../assets/images/viewer.png";
import Camera from "../../assets/images/camera.png";
import SchedulesIcon from "../../assets/images/schedules.png";
import ItpIcon from "../../assets/images/checklists.png";
import AssetManagerVideo from "../../assets/videos/Asset Manager.mp4";
import ITPVideo from "../../assets/videos/ITP.mp4";
import MaintenanceVideo from "../../assets/videos/Recording Maintenance Against Model.mp4";
import ModelViewerVideo from "../../assets/videos/Model Viewer.mp4";
import SchedulesVideo from "../../assets/videos/Schedules2.mp4";
import ObjectManagerVideo from "../../assets/videos/Object.mp4";
import PIDVideo from "../../assets/videos/PID App.mp4";
import MarkupVideo from "../../assets/videos/Markup.mp4";
import Pix4DVideo from "../../assets/videos/pix4d.mp4";

const moduleIcons = {
  "site-maintenance": SiteMaintenanceIcon,
  "asset-manager": AssetManagerIcon,
  "object-manager": ObjectManagerIcon,
  pid: PidIcon,
  "model-viewer": ModelViewerIcon,
  "pix-4d": Camera,
  schedules: SchedulesIcon,
  itp: ItpIcon,
};

const moduleVideos = {
  "asset-manager": [AssetManagerVideo],
  itp: [ITPVideo],
  "site-maintenance": [MaintenanceVideo],
  "model-viewer": [ModelViewerVideo, MarkupVideo],
  schedules: [SchedulesVideo],
  "object-manager": [ObjectManagerVideo],
  pid: [PIDVideo],
  "pix-4d": [Pix4DVideo],
};

export default function OrbDashboard() {
  const orbRingRadius = 300;
  const innerArcsRadius = orbRingRadius * 0.8;
  const moduleRadius = (orbRingRadius + innerArcsRadius) / 2;
  const meteorSpinDurationSeconds = 18;
  const nodeDiameterPx = 48;
  const meteorHeadDiameterPx = 8;
  const pingDurationMultiplier = 1.6;
  const orbitCircumference = 2 * Math.PI * moduleRadius;
  const meteorContactHalfWindow =
    ((nodeDiameterPx / 2 + meteorHeadDiameterPx / 2) * pingDurationMultiplier) /
    orbitCircumference;
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedModuleVideoIndex, setSelectedModuleVideoIndex] = useState(0);
  const [meteorPhase, setMeteorPhase] = useState(0);
  const [detailView, setDetailView] = useState("overview");
  const selectedModuleVideos = selectedModule
    ? moduleVideos[selectedModule.key] || []
    : [];
  const selectedModuleVideo =
    selectedModuleVideos[selectedModuleVideoIndex] || null;

  const selectModule = (mod) => {
    setSelectedModule(mod);
    setSelectedModuleVideoIndex(0);
    setDetailView("overview");
  };

  const closeModule = () => {
    setSelectedModule(null);
    setSelectedModuleVideoIndex(0);
    setDetailView("overview");
  };

  const showPreviousVideo = () => {
    if (selectedModuleVideos.length < 2) {
      return;
    }

    setSelectedModuleVideoIndex((prev) => {
      if (prev === 0) {
        return selectedModuleVideos.length - 1;
      }

      return prev - 1;
    });
  };

  const showNextVideo = () => {
    if (selectedModuleVideos.length < 2) {
      return;
    }

    setSelectedModuleVideoIndex((prev) =>
      prev === selectedModuleVideos.length - 1 ? 0 : prev + 1,
    );
  };

  const handleMapBackdropClick = (event) => {
    if (!selectedModule) {
      return;
    }

    closeModule();
  };

  useEffect(() => {
    let frameId;
    const startTime = performance.now();

    const tick = (now) => {
      const elapsedSeconds =
        ((now - startTime) / 1000) % meteorSpinDurationSeconds;
      setMeteorPhase(elapsedSeconds / meteorSpinDurationSeconds);
      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [meteorSpinDurationSeconds]);

  return (
    <div className="orb-container">
      <div
        className={`orb-map ${selectedModule ? "orb-map--dimmed" : ""}`}
        onClick={handleMapBackdropClick}
      >
        <div className="orb-wrapper">
          {/* Outer glow ring */}
          <div className="orb-ring"></div>

          <div
            className="module-ring__meteor-track"
            style={{
              transform: `rotate(${meteorPhase * 360}deg)`,
            }}
            aria-hidden="true"
          >
            <span className="governance-ring__arrow module-ring__meteor"></span>
          </div>

          {/* Inner arcs */}
          <div className="inner-arcs"></div>

          <div className="governance-ring" aria-hidden="true">
            <svg className="governance-ring__svg" viewBox="0 0 100 100">
              <defs>
                <path
                  id="governance-ring-path"
                  d="M 50,50 m -34,0 a 34,34 0 1,1 68,0 a 34,34 0 1,1 -68,0"
                />
              </defs>

              <circle
                className="governance-ring__stroke"
                cx="50"
                cy="50"
                r="34"
              />

              <text className="governance-ring__text" dy="-1.4">
                <textPath
                  href="#governance-ring-path"
                  startOffset="25%"
                  textAnchor="middle"
                >
                  EMBEDDED GOVERNANCE
                </textPath>
              </text>
            </svg>
          </div>

          {/* Center */}
          <div className="center-core">
            <img
              className="core-logo"
              src={MWHLoadingScreenLogo}
              alt="MWH Loading Screen Logo"
            />
          </div>

          {/* Orbit Items */}
          {modules.map((mod, index) => {
            const angleStep = 360 / modules.length;
            const angleDeg = -90 + angleStep / 2 + angleStep * index;
            const rad = (angleDeg * Math.PI) / 180;

            const x = moduleRadius * Math.cos(rad);
            const y = moduleRadius * Math.sin(rad);
            const nodePhase = (index + 0.5) / modules.length;
            const phaseDelta = Math.abs(meteorPhase - nodePhase);
            const wrappedPhaseDelta = Math.min(phaseDelta, 1 - phaseDelta);
            const isMeteorHit = wrappedPhaseDelta <= meteorContactHalfWindow;

            const labelDirection = x >= 0 ? "right" : "left";

            return (
              <div
                key={mod.key || index}
                className="orb-item"
                style={{
                  transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
                }}
              >
                <button
                  className="orb-item-button"
                  type="button"
                  onClick={() => selectModule(mod)}
                >
                  <div
                    className={`node ${isMeteorHit ? "node--meteor-hit-active" : ""} ${mod.active ? "active" : ""}`}
                  >
                    <img
                      className="node-icon"
                      src={moduleIcons[mod.key]}
                      alt={`${mod.title} icon`}
                    />
                  </div>

                  <div
                    className={`label label--${labelDirection} ${mod.active ? "active-box" : ""}`}
                  >
                    <h6>{mod.title}</h6>
                    <p className="label-summary">{mod.summary}</p>

                    {mod.active && (
                      <ul>
                        {mod.points.map((point) => (
                          <li key={point}>{point}</li>
                        ))}
                      </ul>
                    )}

                    <div className="underline"></div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <section
        className={`orb-detail-page ${selectedModule ? "orb-detail-page--open" : ""}`}
      >
        {selectedModule && (
          <>
            <div className="orb-detail-page__header">
              <p className="orb-detail-page__eyebrow">mView Feature</p>
              <div className="orb-detail-page__title-row">
                <h3>{selectedModule.title}</h3>
                <div
                  className="orb-detail-page__meta"
                  aria-label="Module metadata"
                >
                  <span className="orb-detail-page__meta-label">
                    Release Date
                  </span>
                  <span className="orb-detail-page__meta-value">
                    {selectedModule.releaseDate || "TBC"}
                  </span>
                </div>
              </div>
              <div className="orb-detail-page__actions">
                <button
                  className={`home-btn home-btn--ghost orb-detail-page__plan-btn ${detailView === "plan" ? "orb-detail-page__plan-btn--active" : ""}`}
                  type="button"
                  onClick={closeModule}
                >
                  Back to Orb
                </button>
                {["model-viewer"].includes(selectedModule.key) && (
                <Link className="home-btn home-btn--solid" to={selectedModule.launch}>
                  Launch
                </Link>                  
)}

                {/* <button
                  className={`home-btn home-btn--ghost orb-detail-page__plan-btn ${detailView === "plan" ? "orb-detail-page__plan-btn--active" : ""}`}
                  type="button"
                  onClick={() =>
                    setDetailView((prevView) =>
                      prevView === "plan" ? "overview" : "plan",
                    )
                  }
                  aria-pressed={detailView === "plan"}
                >
                  {detailView === "plan" ? "Details" : "Plan"}
                </button> */}
              </div>
            </div>

            {detailView === "plan" ? (
              <ModulePlan module={selectedModule} />
            ) : (
              <>
                <p className="orb-detail-page__summary">
                  {selectedModule.summary}
                </p>

                <ul className="orb-detail-page__list">
                  {selectedModule.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>

                {selectedModuleVideo && (
                  <div className="orb-detail-page__video-wrap">
                    {selectedModuleVideos.length > 1 && (
                      <>
                        <button
                          className="orb-detail-page__video-nav orb-detail-page__video-nav--left"
                          type="button"
                          onClick={showPreviousVideo}
                          aria-label="Show previous video"
                        >
                          {"<"}
                        </button>

                        <button
                          className="orb-detail-page__video-nav orb-detail-page__video-nav--right"
                          type="button"
                          onClick={showNextVideo}
                          aria-label="Show next video"
                        >
                          {">"}
                        </button>
                      </>
                    )}

                    <video
                      key={selectedModuleVideo}
                      className="orb-detail-page__video"
                      controls
                      preload="metadata"
                      playsInline
                    >
                      <source src={selectedModuleVideo} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>

                    {selectedModuleVideos.length > 1 && (
                      <p className="orb-detail-page__video-count">
                        Video {selectedModuleVideoIndex + 1} of{" "}
                        {selectedModuleVideos.length}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}
